import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Play, Volume2, Clock, Newspaper, FileText, Image as ImageIcon, Download, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaSection = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [pressArticles, setPressArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMediaData();
  }, []);

  const loadMediaData = async () => {
    try {
      const [videosRes, audiosRes, pressRes] = await Promise.all([
        axios.get(`${API}/public/videos`),
        axios.get(`${API}/public/audios`),
        axios.get(`${API}/public/press`)
      ]);
      setVideos(videosRes.data);
      setAudios(audiosRes.data);
      setPressArticles(pressRes.data);
    } catch (error) {
      console.error('Error loading media data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openVideo = (videoId) => {
    setSelectedVideo(videoId);
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="h-6 w-6 text-red-600" />;
      case 'jpg':
      case 'png':
        return <ImageIcon className="h-6 w-6 text-blue-600" />;
      default:
        return <FileText className="h-6 w-6 text-slate-600" />;
    }
  };

  const getFileTypeBadge = (fileType) => {
    const colors = {
      pdf: 'bg-red-100 text-red-700',
      jpg: 'bg-blue-100 text-blue-700',
      png: 'bg-blue-100 text-blue-700'
    };
    return colors[fileType] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  // Don't render if no media content
  if (videos.length === 0 && audios.length === 0 && pressArticles.length === 0) {
    return null;
  }

  return (
    <section className="py-24 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
            Recursos <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">audiovisuals</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
            Vídeos i àudios sobre el tractament i cura de palmeres
          </p>
          <a 
            href="https://www.youtube.com/channel/UCWoWOURwfPm0SyKyWtzn0HQ"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors shadow-lg"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            Visita el nostre canal de YouTube
          </a>
        </div>

        <Tabs defaultValue="videos" className="max-w-6xl mx-auto">
          <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 mb-8">
            <TabsTrigger value="videos">Vídeos</TabsTrigger>
            <TabsTrigger value="audios">Àudios</TabsTrigger>
            <TabsTrigger value="press">Premsa</TabsTrigger>
          </TabsList>

          <TabsContent value="videos">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video) => (
                <Card key={video._id || video.id} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden group bg-white">
                  <div className="relative">
                    {selectedVideo === video.youtube_id ? (
                      <div className="aspect-video">
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1`}
                          title={video.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <div
                        className="relative cursor-pointer"
                        onClick={() => openVideo(video.youtube_id)}
                      >
                        <img
                          src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
                          alt={video.title}
                          className="w-full aspect-video object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => {
                            e.target.src = `https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent flex items-center justify-center group-hover:from-black/70 transition-all">
                          <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                            <Play className="h-10 w-10 text-white ml-1" fill="white" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <CardHeader>
                    <CardTitle className="text-lg font-bold text-slate-900">{video.title}</CardTitle>
                    <CardDescription className="text-slate-600">{video.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="audios">
            <div className="grid md:grid-cols-2 gap-8">
              {audios.map((audio) => (
                <Card key={audio._id || audio.id} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                        <Volume2 className="h-8 w-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl font-bold text-slate-900 mb-2">{audio.title}</CardTitle>
                        <CardDescription className="text-slate-600">{audio.description}</CardDescription>
                        <div className="flex items-center gap-2 mt-3 text-sm text-emerald-600 font-medium">
                          <Clock className="h-4 w-4" />
                          <span>{audio.duration}</span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <audio controls className="w-full">
                      <source src={audio.url} type="audio/mpeg" />
                      El vostre navegador no suporta l'element d'àudio.
                    </audio>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="press">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pressArticles.map((article) => (
                <Card key={article._id || article.id} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white overflow-hidden group">
                  <div className="relative">
                    <img
                      src={article.file_url}
                      alt={article.title}
                      className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getFileTypeBadge(article.file_type)}`}>
                        {article.file_type}
                      </span>
                    </div>
                  </div>
                  <CardHeader>
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        {getFileIcon(article.file_type)}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">
                          {article.source}
                        </p>
                        {article.published_date && (
                          <p className="text-xs text-slate-500">{article.published_date}</p>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-lg font-bold text-slate-900 line-clamp-2">
                      {article.title}
                    </CardTitle>
                    <CardDescription className="text-slate-600 line-clamp-3">
                      {article.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="flex-1 border-amber-600 text-amber-700 hover:bg-amber-50"
                      >
                        <a href={article.file_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Veure
                        </a>
                      </Button>
                      <Button
                        asChild
                        size="sm"
                        className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                      >
                        <a href={article.file_url} download>
                          <Download className="h-4 w-4 mr-2" />
                          Descarregar
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default MediaSection;
